import { TRADE, CONSUMER } from 'dibs-buyer-layout/exports/userTypeEnums';
import { PAGE_SIZE_OPTIONS } from '../constants/sbConstants';
import { getProductTileAdData } from 'dibs-google-ad-manager/exports/adHelpers';
import { type AdProductTile, type AdDeviceType } from 'dibs-google-ad-manager/exports/types';

/* Constants */
import {
    QP_PAGE_SIZE_VALUE_DEFAULT,
    QP_PAGE_SIZE_VALUE_MAX,
} from '../constants/queryParamConstants';

type UserType = typeof TRADE | typeof CONSUMER;

const adLimitMap = PAGE_SIZE_OPTIONS.reduce<{ [n: number]: number }>(
    (map, { size, adLimit }) => ({
        ...map,
        [size]: adLimit,
    }),
    {}
);

function getAdCountForPage({
    pageSize = QP_PAGE_SIZE_VALUE_DEFAULT,
    uriRef,
    userType,
}: {
    pageSize: number;
    uriRef: string;
    userType: UserType;
}): number {
    // deviceType does not matter for ad count, should be the same amount of ads for desktop and mobile
    const numberOfTotalTileAds =
        getProductTileAdData({ uriRef, userType, deviceType: 'desktop' })?.length || 0;
    /*
    const numberOfTotalTileAds = ((getAdData({ uriRef, adKey: TILE_AD_KEY, userType }) ||
        []) as AdProductTile[]).length;
        */

    /**
     * if we cant find a limit in our limit map just use the number of ads from the ad map
     * adLimitMap is keyed by page size selected (120, 60) not number of tiles shown (ie 116, 58)
     */
    const adLimit = adLimitMap[pageSize] || numberOfTotalTileAds;
    const numberOfTileAds = numberOfTotalTileAds <= adLimit ? numberOfTotalTileAds : adLimit;

    return numberOfTileAds;
}

export function getNumberOfTiles({
    path,
    pageSize = QP_PAGE_SIZE_VALUE_DEFAULT,
    isSbAdsEnabled = true,
    isDesktop = true,
    userType = CONSUMER,
}: {
    path: string;
    pageSize: number;
    isSbAdsEnabled: boolean;
    isDesktop: boolean;
    userType: UserType;
}): number {
    if (isSbAdsEnabled && isDesktop) {
        return pageSize - getAdCountForPage({ uriRef: path, pageSize, userType });
    }
    return pageSize;
}

function getProductTileAdIndex(
    adIndex: number,
    pageCol: number = 3,
    startRow: number = 4,
    gapRows: number = 8
): number {
    // ad position offset. Moves the ad from left to right of row
    // used to move ad in the middle row
    const offset = pageCol % 2 === 0 ? 0 : adIndex;

    // finds last position of a row
    const rowPosition = pageCol * (startRow + gapRows * adIndex);

    return rowPosition - offset - 1;
}

function shouldGetProductTileAds({
    numOfTiles,
    isSbAdsEnabled,
    deviceType,
}: {
    numOfTiles: number;
    isSbAdsEnabled?: boolean | null;
    deviceType: AdDeviceType;
}): boolean {
    if (!isSbAdsEnabled) {
        return false;
    }
    if (deviceType === 'mobile') {
        return true;
    }
    if (numOfTiles === QP_PAGE_SIZE_VALUE_DEFAULT || numOfTiles === QP_PAGE_SIZE_VALUE_MAX) {
        return false;
    }

    return true;
}

export const getPageSizeOfTiles = ({
    uriRef,
    numOfTiles,
    userType,
}: {
    uriRef: string;
    numOfTiles: number;
    userType: UserType;
}): number => {
    // tilesToPageSizeMapForPage is indexed by numb of tiles (pageSize - adCount) to pageSize
    // e.g.{ 58: 60, 116: 120 } or { 58: 60, 118: 120 } (for art only because it has 2 ads max) or { 60: 60, 120: 120 }
    const tilesToPageSizeMapForPage = PAGE_SIZE_OPTIONS.reduce<{ [i: number]: number }>(
        (map, { size }) => {
            const adCount = getAdCountForPage({ uriRef, pageSize: size, userType });
            return {
                ...map,
                [size - adCount]: size,
            };
        },
        {}
    );

    return tilesToPageSizeMapForPage[numOfTiles];
};

export const getProductTileAdMap = ({
    uriRef,
    pageCol,
    startRow,
    gapRows,
    pageSize: numOfTiles,
    userType,
    deviceType,
    isSbAdsEnabled,
}: {
    uriRef: string;
    pageCol?: number;
    startRow?: number;
    gapRows?: number;
    pageSize: number;
    userType: UserType;
    deviceType: AdDeviceType;
    isSbAdsEnabled?: boolean | null;
}): { [n: number]: AdProductTile } => {
    if (!numOfTiles || !shouldGetProductTileAds({ numOfTiles, isSbAdsEnabled, deviceType })) {
        return {};
    }

    const productTileAdData = getProductTileAdData({ uriRef, userType, deviceType });

    const adLimit = getAdCountForPage({
        uriRef,
        pageSize: getPageSizeOfTiles({ uriRef, numOfTiles, userType }), // we need to map numOfTiles back to original pageSize to get correct adLimit
        userType,
    });

    return (
        (productTileAdData || [])
            // limit by number of ads per page size
            .slice(0, adLimit)
            // get index
            .reduce<{ [n: number]: AdProductTile }>((map, data, i) => {
                return {
                    ...map,
                    [getProductTileAdIndex(i, pageCol, startRow, gapRows)]: data,
                };
            }, {})
    );
};
